import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "submission-and-review"
    }}>{`Submission and review`}</h1>
    <p>{`Congratulations! You've built a great Adobe integration and you're ready to release it to your customers.`}</p>
    <p>{`To be granted production status for a Client ID, `}<strong parentName="p">{`your integration must first go through a review process`}</strong>{`.`}</p>
    <p>{`By reviewing every integration, Adobe aims to help developers get ready for primetime and ensure that users have great experiences with the integrations they consume.`}</p>
    <p>{`This document will give you an idea of what we’re checking for during the review process. While lengthy, working through the points below will help you make sure you’ve accounted for all of the requirements, and avoid having to fix things and resubmit before being granted production status.`}</p>
    <h2 {...{
      "id": "pre-submission-checklist"
    }}>{`Pre-submission checklist`}</h2>
    <p>{`Our goal is to balance providing you the best developer experience during review that we can, while helping ensure that approved integrations offer a great user experience for our mutual customers.`}</p>
    <p>{`This list will give you an overview of the kinds of things we check for during review. The list will change over time, and cannot cover absolutely everything we look at.`}</p>
    <h3 {...{
      "id": "metadata"
    }}>{`Metadata`}</h3>
    <p>{`As part of submission, you will enter two kinds of metadata on the `}<a parentName="p" {...{
        "href": "https://console.adobe.io"
      }}>{`Adobe Developer Console`}</a>{`: a publisher profile and details about the specific integration you are submitting.`}</p>
    <p>{`For specifics regarding character lengths, image dimensions, and required fields, always refer to the Adobe Developer Console submission form for the latest requirements.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`We recommend that you draft this metadata in a text editor, and not directly in the Adobe Developer Console. This will help you avoid losing your writing if your browser refreshes before submission.`}</p>
    <h4 {...{
      "id": "publisher-profile"
    }}>{`Publisher profile`}</h4>
    <p>{`You publisher profile is about you as a company or developer as whole. Once you've submitted this information and it has been approved by Adobe, it is shown for `}<em parentName="p">{`all plugins and integrations you publish with Adobe`}</em>{`.`}</p>
    <p>{`Your publisher profile includes required details like:`}</p>
    <ul>
      <li parentName="ul">{`Your publisher public name (often company name or, for individuals, your own name)`}</li>
      <li parentName="ul">{`Your publisher marketing website (usually not the same as your "App website", which is covered in the next section)`}</li>
      <li parentName="ul">{`A description of you, the publisher`}</li>
      <li parentName="ul">{`A logo`}</li>
    </ul>
    <p><strong parentName="p">{`Your publisher profile is an important thing to get right the first time!`}</strong>{` After it is approved, changing it is not easy or fast: you will have to email us via the "View public profile" link on your `}<a parentName="p" {...{
        "href": "https://console.adobe.io"
      }}>{`Adobe Developer Console`}</a>{` project. Doing so will trigger a new review of your publisher details, and possibly of your integration as well.`}</p>
    <p>{`Frequently changing your publisher profile will confuse users and erode trust. Please be sure you're ready before entering this information.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p><strong parentName="p">{`You must complete your publisher profile`}</strong>{` in order to submit your first integration. Next time you submit an integration, your publisher profile will already be set, so you won't need to do this again.`}</p>
    <h4 {...{
      "id": "integration-details"
    }}>{`Integration details`}</h4>
    <p>{`Your integration details are information you provide to Adobe, and in some cases your users, about the specific integration you are currently submitting. Each integration you submit will have its own set of integration details that you provide.`}</p>
    <p>{`Always check the `}<a parentName="p" {...{
        "href": "https://console.adobe.io"
      }}>{`Adobe Developer Console`}</a>{` for the latest list of required details. At the time of writing, those details include the following lists.`}</p>
    <p><strong parentName="p">{`Consent screen details`}</strong></p>
    <p>{`The consent screen details appear on the post-login Adobe authorization screen to help users make an informed decision when granting access to your integration.`}</p>
    <ul>
      <li parentName="ul">{`Public app name`}</li>
      <li parentName="ul">{`App website`}</li>
      <li parentName="ul">{`Privacy policy`}</li>
      <li parentName="ul">{`Terms of use`}</li>
    </ul>
    <p><strong parentName="p">{`Approval details`}</strong></p>
    <p>{`The approval details help Adobe learn more about the integration you are submitting.`}</p>
    <ul>
      <li parentName="ul">{`App description`}</li>
      <li parentName="ul">{`Publish to Adobe Enterprise Admin Console?`}</li>
      <li parentName="ul">{`Notes to reviewer`}</li>
      <li parentName="ul">{`Screenshots`}</li>
    </ul>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p><strong parentName="p">{`Test accounts:`}</strong>{` If your app or service requires a user account to test the Adobe integration, you must provide credentials for a test account in the "Notes to reviewer" field. If we are unable to access the integration, the submission will be rejected and you will need to resubmit.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p><strong parentName="p">{`Instructions and screenshots:`}</strong>{` We see a lot of integrations! It's possible this is the first time we're seeing your app (welcome!), or maybe we haven't seen it in a while (welcome back!). Please help our review team find the Adobe integration within your app by providing detailed `}<strong parentName="p">{`instructions`}</strong>{` and `}<strong parentName="p">{`screenshots`}</strong>{`. If we are unable to find the integration, the submission will be rejected and you will need to resubmit.`}</p>
    <h3 {...{
      "id": "auth"
    }}>{`Auth`}</h3>
    <p>{`When an integration involves a logged in Adobe user, we will be reviewing the experience related to authentication and authorization.`}</p>
    <h4 {...{
      "id": "authentication"
    }}>{`Authentication`}</h4>
    <p>{`Integrations that require access to a logged in Adobe user and their stored data should implement an appropriate authentication workflow, usually OAuth 2.0.`}</p>
    <p>{`The user must also be able to log out of their Adobe account from your integration.`}</p>
    <p>{`Your users can also revoke access from your application on the `}<a parentName="p" {...{
        "href": "https://account.adobe.com/connected-accounts"
      }}>{`Connected accounts page`}</a>{` of their Adobe account.`}</p>
    <h4 {...{
      "id": "authorization"
    }}>{`Authorization`}</h4>
    <p>{`After the user first logs in to your integration, they will be prompted to authorize access to their data with a consent screen.`}</p>
    <p>{`The consent screen must present correct and complete information to the user about you, the publisher, and the integration. This data is set when you submit your publisher profile and integration details via the `}<a parentName="p" {...{
        "href": "https://console.adobe.io"
      }}>{`Adobe Developer Console`}</a>{` (see the Metadata section of this page for details).`}</p>
    <h4 {...{
      "id": "scopes"
    }}>{`Scopes`}</h4>
    <p>{`The auth scopes your integration uses will determine the level of access the user is asked to grant on the consent screen. We require that all integrations use the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Principle_of_least_privilege"
      }}>{`principle of least privilege`}</a>{`; never ask for more access than you need.`}</p>
    <p>{`Integrations asking for more access than necessary `}<em parentName="p">{`at the time of submission`}</em>{` will be rejected.`}</p>
    <p>{`The required scopes for Creative Cloud Libraries are:`}</p>
    <pre><code parentName="pre" {...{}}>{`openid,creative_sdk,profile,address,AdobeID,email,cc_files,cc_libraries
`}</code></pre>
    <p>{`There is also an optional scope, `}<inlineCode parentName="p">{`offline_access`}</inlineCode>{` which will provide a refresh token that can be used to generate an access token for up to 2 weeks after the user has logged in.`}</p>
    <h3 {...{
      "id": "content"
    }}>{`Content`}</h3>
    <p>{`Content within, and associated with, your integration with be reviewed.`}</p>
    <h4 {...{
      "id": "adobe-branding"
    }}>{`Adobe Branding`}</h4>
    <p>{`See our `}<a parentName="p" {...{
        "href": "../marketing"
      }}>{`"Marketing your integration"`}</a>{` page for guidelines on using Adobe brand assets and messaging your integration to customers.`}</p>
    <h4 {...{
      "id": "inappropriate-content"
    }}>{`Inappropriate content`}</h4>
    <p>{`Your integration and associated marketing surfaces must not include any intense violence, blood, gore, sexual content, nudity, or strong language.`}</p>
    <p>{`Your integration must not promote or conduct phishing, spamming, hacking, password trafficking, or spyware, nor contain malware, trojans, or viruses.`}</p>
    <h3 {...{
      "id": "user-experience"
    }}>{`User experience`}</h3>
    <p>{`Checking the integration's user experience is the core of our review process.`}</p>
    <p>{`Since all applications and integrations are different, there are fewer concrete guidelines we can provide. However, we are generally looking at these three things: functionality, user interface, and performance.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Functionality:`}</strong>{` Does the integration do what it suggests in the associated content and the user interface?`}</li>
      <li parentName="ul"><strong parentName="li">{`User interface:`}</strong>{` Is the user able to find the integration and complete basic tasks? Is the UI broken or distorted in any way? Is the user made aware of what is (or isn't) happening?`}</li>
      <li parentName="ul"><strong parentName="li">{`Performance:`}</strong>{` Are system or account resources misused or overused? Is the user left waiting for an abnormal amount of time?`}</li>
    </ul>
    <h2 {...{
      "id": "how-to-submit"
    }}>{`How to submit`}</h2>
    <p>{`You can submit your integration for approval and review on the `}<a parentName="p" {...{
        "href": "https://console.adobe.io"
      }}>{`Adobe Developer Console`}</a>{`.`}</p>
    <p>{`The following is a basic overview of the steps:`}</p>
    <ol>
      <li parentName="ol"><a parentName="li" {...{
          "href": "https://console.adobe.io"
        }}>{`Visit the Adobe Developer Console`}</a></li>
      <li parentName="ol">{`Go to the `}<em parentName="li">{`Projects`}</em>{` tab`}</li>
      <li parentName="ol">{`Find and select the project that contains the Client ID you are seeking production approval for`}</li>
      <li parentName="ol">{`On your project page, select `}<em parentName="li">{`Approval`}</em></li>
      <li parentName="ol">{`(First time only) Complete your publisher profile via the "View public profile" button, and save`}</li>
      <li parentName="ol">{`Fill out the "Submit for approval" form, and submit`}</li>
    </ol>
    <p>{`Further details on the data you are required to sumbit can be found in the `}<a parentName="p" {...{
        "href": "#pre-submission-checklist"
      }}>{`Pre-submission checklist`}</a>{` section of this page.`}</p>
    <h2 {...{
      "id": "timelines-and-outcomes"
    }}>{`Timelines and outcomes`}</h2>
    <p>{`We aim to respond to all submissions within 10 business days, but we are typically much faster on average.`}</p>
    <p>{`Ultimately, your submission will either be approved or rejected. In some cases, we may reach out to you with questions before we send you the outcome of the review.`}</p>
    <p>{`If your submission is rejected, we will provide the reasons. You can ask for clarifications, if you need any. Once you have addressed the reasons for rejection in your integration and associated content, you are welcome to resubmit.`}</p>
    <p>{`Again, our goal is to provide you the best developer experience during review that we can, while helping ensure that approved integrations offer a great user experience for our mutual customers.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      